import { useState, useEffect, useCallback } from 'react';
import { isDivisionLevel, DIVISION_VALUES } from '../utils/Constants';

// --- Custom Hook: useLocalStorageLevel ---
// Manages the user's exam selection — `level` plus an optional secondary dimension:
// `division` (SSC/HSC: Science/Arts/Commerce) or `unit` (VARSITY-*: A–E) — synced with
// localStorage and broadcast across components/tabs.
//
// Backward compatible: existing callers use `const [level, setLevel] = useLocalStorageLevel()`.
// `setLevel(newLevel)` sets the level and CLEARS any division/unit (a plain level pick) —
// except SSC/HSC, which default to `Science` so a bare level pick still carries a division.
// New callers can read the 3rd return value: { division, unit, setSelection }.
const LEVEL_KEY = 'level';
const DIVISION_KEY = 'division';
const UNIT_KEY = 'unit';
const CHANGE_EVENT = 'localStorageLevelChange';
const DEFAULT_DIVISION = DIVISION_VALUES[0]; // 'Science'

// Apply the SSC/HSC default division so those levels always carry one.
function withDefaults({ level, division, unit }) {
    const lvl = level || '';
    let div = division || '';
    const un = unit || '';
    if (isDivisionLevel(lvl) && !div) div = DEFAULT_DIVISION;
    return { level: lvl, division: div, unit: un };
}

function readAll() {
    try {
        return withDefaults({
            level: localStorage.getItem(LEVEL_KEY) || '',
            division: localStorage.getItem(DIVISION_KEY) || '',
            unit: localStorage.getItem(UNIT_KEY) || '',
        });
    } catch (error) {
        console.error('Error reading level from localStorage:', error);
        return { level: '', division: '', unit: '' };
    }
}

function writeAll({ level, division, unit }) {
    try {
        localStorage.setItem(LEVEL_KEY, level || '');
        localStorage.setItem(DIVISION_KEY, division || '');
        localStorage.setItem(UNIT_KEY, unit || '');
        // Notify other components in the SAME tab (native 'storage' only fires cross-tab).
        window.dispatchEvent(new CustomEvent(CHANGE_EVENT, {
            detail: { level: level || '', division: division || '', unit: unit || '' },
        }));
    } catch (error) {
        console.error('Error writing level to localStorage:', error);
    }
}

export default function useLocalStorageLevel() {
    const [selection, setSelectionState] = useState(readAll);

    // Legacy setter: set the level, clearing any division/unit (SSC/HSC default to Science).
    const updateLevel = useCallback((newLevel) => {
        const next = withDefaults({ level: newLevel || '', division: '', unit: '' });
        setSelectionState(next);
        writeAll(next);
    }, []);

    // Full setter: set level + optional secondary dimension in one go.
    const setSelection = useCallback((newLevel, { division = '', unit = '' } = {}) => {
        const next = withDefaults({ level: newLevel || '', division, unit });
        setSelectionState(next);
        writeAll(next);
    }, []);

    useEffect(() => {
        // Cross-tab: any of the three keys changed elsewhere → re-read all.
        const handleStorageChange = (event) => {
            if ([LEVEL_KEY, DIVISION_KEY, UNIT_KEY].includes(event.key)) {
                setSelectionState(readAll());
            }
        };
        // Same-tab: our custom event carries the full selection.
        const handleCustomLevelChange = (event) => {
            const d = event.detail || {};
            setSelectionState({
                level: d.level || '',
                division: d.division || '',
                unit: d.unit || '',
            });
        };
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener(CHANGE_EVENT, handleCustomLevelChange);
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener(CHANGE_EVENT, handleCustomLevelChange);
        };
    }, []);

    // [level, setLevel] stays backward compatible; 3rd element carries the extras.
    return [
        selection.level,
        updateLevel,
        { division: selection.division, unit: selection.unit, setSelection },
    ];
}
